import { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { changePassword } from "../../services/client";

const useStyles = makeStyles((theme) => {
  const fieldRadius = theme.borderRadiusBoxes?.borderRadius || 4;
  const buttonRadius = fieldRadius;

  return {
    form: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    introCard: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1.25),
      padding: theme.spacing(1.5, 1.75),
      borderRadius: fieldRadius,
      border: `1px solid ${theme.palette.border.soft}`,
      background: `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${theme.palette.surface.soft} 100%)`,
      boxShadow: theme.palette.shadow.card,
    },
    introIcon: {
      width: 32,
      height: 32,
      minWidth: 32,
      borderRadius: fieldRadius,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.surface.accentSoft,
      border: `1px solid ${theme.palette.surface.accentBorder}`,
    },
    introText: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.25),
      minWidth: 0,
    },
    introTitle: {
      color: theme.palette.text.primary,
      fontWeight: 700,
      fontSize: 14,
      lineHeight: 1.4,
    },
    introDescription: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      lineHeight: 1.5,
    },
    field: {
      width: "100%",
      borderRadius: fieldRadius,
      "& .MuiInputBase-root": {
        backgroundColor: theme.palette.common.white,
        borderRadius: fieldRadius,
        overflow: "hidden",
        transition: "box-shadow 160ms ease, border-color 160ms ease",
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: fieldRadius,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.border.input,
        borderRadius: fieldRadius,
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.light,
      },
      "& .MuiOutlinedInput-root.Mui-focused": {
        boxShadow: `0 0 0 3px ${theme.palette.surface.accentSoft}`,
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "& .MuiInputLabel-root": {
        color: theme.palette.text.secondary,
      },
      "& .MuiFormHelperText-root": {
        marginLeft: 0,
      },
      "& .MuiOutlinedInput-input": {
        paddingTop: theme.spacing(1.55),
        paddingBottom: theme.spacing(1.55),
      },
    },
    validationMessage: {
      minHeight: 46,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1.25),
      borderRadius: fieldRadius,
      fontSize: 13,
      lineHeight: 1.5,
      backgroundColor: theme.palette.surface.soft,
      border: `1px solid ${theme.palette.border.soft}`,
      textAlign: "center",
    },
    actions: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: theme.spacing(1.5),
      flexWrap: "wrap",
      marginTop: theme.spacing(1),
    },
    secondaryButton: {
      borderRadius: buttonRadius,
      overflow: "hidden",
      textTransform: "none",
      fontWeight: 600,
      boxShadow: "none",
      minWidth: 140,
      padding: theme.spacing(1, 2.25),
      borderColor: theme.palette.border.button,
      color: theme.palette.text.secondary,
    },
    primaryButton: {
      borderRadius: buttonRadius,
      overflow: "hidden",
      textTransform: "none",
      fontWeight: 600,
      boxShadow: "none",
      minWidth: 140,
      padding: theme.spacing(1, 2.5),
    },
  };
});

const PasswordChange = ({ handleOpenPasswordChange, setMsg, setClose }) => {
  const classes = useStyles();
  const [paswordState, setPasswordState] = useState({
    currentPassword: "",
    onePassword: "",
    secondPassword: "",
  });
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [error, setError] = useState({
    color: "",
    msg: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (paswordState.currentPassword.length > 0) {
      if (paswordState.onePassword !== paswordState.secondPassword) {
        setButtonDisabled(true);
        setError({
          color: "red",
          msg: "Las contrasenas deben coincidir y tener mas de 4 caracteres.",
        });
      } else if (paswordState.onePassword.length > 4 && paswordState.secondPassword.length > 4) {
        setButtonDisabled(false);
        setError({
          color: "green",
          msg: "Validacion correcta.",
        });
      } else {
        setButtonDisabled(true);
        setError({
          color: "red",
          msg: "La nueva contrasena debe tener al menos 5 caracteres.",
        });
      }
    } else {
      setButtonDisabled(true);
      setError({
        color: "green",
        msg: "",
      });
    }
  }, [paswordState]);

  const handleSubmit = async () => {
    try {
      const body = {
        current_password: paswordState.currentPassword,
        new_password: paswordState.onePassword,
      };
      await changePassword(body);
      setClose(true);
      setMsg({
        msg: "Se ha cambiado su contrasena con exito.",
        type: "success",
      });
    } catch (errorResponse) {
      let message = "Ocurrio un error inesperado.";
      if (errorResponse?.response?.data?.msg) {
        message = errorResponse?.response?.data?.msg;
      }
      setClose(true);
      setMsg({
        msg: message,
        type: "error",
      });
    }

    handleOpenPasswordChange();
  };

  const handleChange = (e) => {
    if (e.target.name === "current_password") {
      setPasswordState({
        ...paswordState,
        currentPassword: e.target.value,
      });
    }
    if (e.target.name === "one_validation") {
      setPasswordState({
        ...paswordState,
        onePassword: e.target.value,
      });
    }
    if (e.target.name === "second_validation") {
      setPasswordState({
        ...paswordState,
        secondPassword: e.target.value,
      });
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <Box className={classes.form}>
      <Box className={classes.introCard}>
        <Box className={classes.introIcon}>
          <LockOutlinedIcon style={{ fontSize: 17 }} />
        </Box>
        <Box className={classes.introText}>
          <Typography className={classes.introTitle}>Actualiza tu contrasena</Typography>
          <Typography className={classes.introDescription}>
            Ingresa tu contrasena actual y luego define una nueva para mantener tu cuenta segura.
          </Typography>
        </Box>
      </Box>

      <TextField
        type={showPassword ? "text" : "password"}
        placeholder="Contrasena actual"
        label="Contrasena actual"
        name="current_password"
        value={paswordState.currentPassword}
        onChange={handleChange}
        fullWidth
        color="primary"
        variant="outlined"
        size="small"
        className={classes.field}
        autoComplete="current-password"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlinedIcon style={{ fontSize: 16 }} color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff style={{ fontSize: 18 }} /> : <Visibility style={{ fontSize: 18 }} />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        placeholder="Nueva contrasena"
        label="Nueva contrasena"
        type={showPassword ? "text" : "password"}
        name="one_validation"
        value={paswordState.onePassword}
        onChange={handleChange}
        fullWidth
        color="primary"
        variant="outlined"
        size="small"
        className={classes.field}
        autoComplete="new-password"
        disabled={paswordState.currentPassword.length === 0}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKeyOutlinedIcon style={{ fontSize: 16 }} color="action" />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        type={showPassword ? "text" : "password"}
        placeholder="Repite la nueva contrasena"
        label="Repite la nueva contrasena"
        name="second_validation"
        value={paswordState.secondPassword}
        onChange={handleChange}
        fullWidth
        color="primary"
        variant="outlined"
        size="small"
        className={classes.field}
        autoComplete="new-password"
        disabled={paswordState.currentPassword.length === 0}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKeyOutlinedIcon style={{ fontSize: 16 }} color="action" />
            </InputAdornment>
          ),
        }}
      />

      <Box className={classes.validationMessage} style={{ color: error.color }}>
        {error.msg ? (
          <>
            {error.color === "green" ? (
              <CheckCircleOutlineOutlinedIcon style={{ fontSize: 16 }} />
            ) : (
              <ErrorOutlineOutlinedIcon style={{ fontSize: 16 }} />
            )}
            <Typography component="span" style={{ color: "inherit", fontSize: "inherit" }}>
              {error.msg}
            </Typography>
          </>
        ) : (
          <Typography component="span" style={{ color: "inherit", fontSize: "inherit" }}>
            Completa los campos para continuar.
          </Typography>
        )}
      </Box>

      <Box className={classes.actions}>
        <Button
          className={classes.secondaryButton}
          variant="outlined"
          onClick={handleOpenPasswordChange}
        >
          Cancelar
        </Button>
        <Button
          disabled={buttonDisabled}
          className={classes.primaryButton}
          color="primary"
          variant="contained"
          onClick={handleSubmit}
        >
          Guardar
        </Button>
      </Box>
    </Box>
  );
};

export default PasswordChange;
